/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef, } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Button, Typography, MenuItem, TextField, Select, CardContent } from "@mui/material"; // assuming you're using Material-UI for Box and Button
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// API
import { detailRP4BillData, solarAnalysisData, detailSmartMeterBillData, SMsolarAnalysisData, reading_values } from "../api/bill";
import { fetchSiteDetails } from './../api/plant';
import { currentBatterySOC } from "../api/meter";
import { sm_othersDataAPI, } from "../api/smartmeter";

// Icon and images
import BrightnessHighIcon from '@mui/icons-material/BrightnessHigh';
import ReceiptIcon from "@mui/icons-material/Receipt";
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import PowerIcon from '@mui/icons-material/Power';
import { InsertChart } from "@mui/icons-material";
import Co2Icon from '@mui/icons-material/Co2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BatteryCharging30Icon from '@mui/icons-material/BatteryCharging30';
import slideD from "../image/slide1.jpg";
import slideL from "../image/slide2.jpg";
import defaultimage from "../image/defaultimage.jpg";
import SavingsIcon from '@mui/icons-material/AttachMoney';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

// Component
import colors from "../colour";
import { useDarkMode } from '../components/DarkModeContext';
import BillView from "../components/billComponent";
import AnalysisSection from "../components/detailAnalysis";
import GraphSection from "../components/graphSection";
import IndividualSection from "../components/IndividualSection";
import MDReportSection from "../components/MDReportSection";

const Dashboard = () => {
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeInterval, setTimeInterval] = useState("15m");
  const [activeView, setActiveView] = useState("graph");
  const [siteId, setSiteId] = useState(null);
  const [currentBill, setcurrentBill] = useState(null);
  const [currentBattery, setCurrentBattery] = useState(null)
  const [currentSolarGeneration, setcurrentSolarGeneration] = useState(null);
  const [userData, setuserData] = useState({});
  const { darkMode } = useDarkMode(); // Access darkMode state
  const [solarViewType, setSolarViewType] = useState('day');
  const [loading, setLoading] = useState(true);
  const [EnergySupplier, setEnergySupplier] = useState(null);
  const [isPPA, SetisPPA] = useState(null);
  const [readingValues, setReading_values] = useState(null);
  const [nem, setNem] = useState();
  const [afa, setafa] = useState();
  const userType = userData?.user_type ?? 1;
  const [activeAnalysisTab, setActiveAnalysisTab] = useState(1);
  // Current Time Function
  const currentDate = new Date();
  // Get date components
  const day = String(currentDate.getDate()).padStart(2, '0');
  const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const year = currentDate.getFullYear();
  // Get time components
  const hours = String(currentDate.getHours()).padStart(2, '0');
  const minutes = String(currentDate.getMinutes()).padStart(2, '0');
  const seconds = String(currentDate.getSeconds()).padStart(2, '0');
  // Format as dd/mm/yyyy hh:mm:ss
  const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  const tariffNameMap = {
    LVG: "Non-Domestic Low Voltage General Tariff",
    LVT: "Non-Domestic Low Voltage ToU Tariff",
    MVG: "Non-Domestic Medium Voltage General Tariff",
    MVT: "Non-Domestic Medium Voltage ToU Tariff",
    HVG: "Non-Domestic High Voltage General Tariff",
    HVT: "Non-Domestic High Voltage ToU Tariff",
    N4: "Low Voltage Industria",
    N5: "High Voltage (33/11kV) General Industrial",
    N6: "High Voltage (33/11kV) Peak / Offpeak Industrial",
    N8: "High Voltage (132kV) Peak / Offpeak Industrial",
  };
  const [showIndividualTab, setShowIndividualTab] = useState(false);
  // const [smOthersData, setsmOthersData] = useState([]);

  // Set `siteId` when `location.state.siteId` changes
  useEffect(() => {
    if (location?.state?.siteId) {
      setSiteId(location?.state?.siteId);
    }
  }, [location?.state?.siteId]);


  // API Call for meter and forecast value
  useEffect(() => {
    if (!siteId || !selectedDate) return;

    const fetchData = async () => {
      const formattedDate = moment(selectedDate).format("YYYY-MM");

      try {
        // 🔹 1. Fetch user info
        const userInfoResp = await fetchSiteDetails(siteId);
        setuserData(userInfoResp);

        // 🔹 2. Use DIRECT response (not state)
        const usertype = userInfoResp?.user_type ?? 1;

        // 🔹 3. Fetch reading values
        const { data: readingData } = await reading_values(siteId, formattedDate);
        setReading_values(readingData);

        const afaValue = readingData?.afa?.toString() || "";
        const nemValue = readingData?.nem?.toString() || "";
        setafa(afaValue);
        setNem(nemValue);

        // 🔹 4. Conditional API calls based on user_type
        if (usertype === 1) {
          // ✅ Solar / Smart Meter
          const [billResp, solarResp] = await Promise.all([
            detailSmartMeterBillData(siteId, formattedDate, afaValue, nemValue),
            SMsolarAnalysisData(siteId, formattedDate, afaValue, nemValue),
          ]);

          setcurrentBill(billResp?.data ?? null);
          setcurrentSolarGeneration(solarResp?.data ?? null);

          // Optional: clear battery if switching from non-solar
          setCurrentBattery(null);

        } else {
          // ✅ Non-Solar / RP4
          const [billResp, solarResp, batteryResp] = await Promise.all([
            detailRP4BillData(siteId, formattedDate),
            solarAnalysisData(siteId, formattedDate),
            currentBatterySOC(siteId),
          ]);

          setcurrentBill(billResp?.data ?? null);
          setcurrentSolarGeneration(solarResp?.data ?? null);
          setCurrentBattery(batteryResp?.data ?? null);
        }

      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [siteId, selectedDate]);


  // Interval Setting
  const TIME_INTERVALS = useMemo(
    () => ({
      "5m": { value: 300, label: "5 Minutes", interval: "5m" },
      "15m": { value: 900, label: "15 Minutes", interval: "15m" },
      "30m": { value: 1800, label: "30 Minutes", interval: "30m" },
      "1h": { value: 3600, label: "1 Hour", interval: "1h" },
    }),
    []
  );

  // Time interval Dictionary
  const timeIntervals = useMemo(
    () => Object.values(TIME_INTERVALS),
    []
  );

  const swiperRef = useRef(null);

  const handleSlideClick = () => {
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.stop();
    }
  };


  useEffect(() => {
    if (userData?.energy_supplier) {
      setEnergySupplier(userData.energy_supplier);
    }
  }, [userData]);
  useEffect(() => {
    if (userData?.is_ppa) {
      SetisPPA(userData.is_ppa);
    }
  }, [userData]);


  //check individual tab data
  const TIME_INTERVAL = useMemo(
    () => ({
      "5m": { value: 300, label: "5 Minute", interval: "5m" },
    }),
    []
  );

  useEffect(() => {
    if (!siteId) return;

    const currenttime = moment().format("YYYY-MM-DD");
    const selectedtime = TIME_INTERVAL[timeInterval]?.value ?? 300;

    const fetchSMData = async () => {
      try {
        const [sm_OthersData] = await Promise.all([
          sm_othersDataAPI(siteId, currenttime, selectedtime),
        ]);

        const data = sm_OthersData?.data ?? [];

        // setsmOthersData(data); // keep this if you're using the data elsewhere
        // ✅ Set true ONLY if data is a non-empty array
        if (Array.isArray(data) && data.length > 0) {
          setShowIndividualTab(true);
        } else {
          setShowIndividualTab(false);
        }

      } catch (error) {
        console.error("Error fetching data:", error);
        setShowIndividualTab(false); // fallback to false on error
      }
    };

    fetchSMData();
  }, [siteId,]);

  //min date for all the graph
  const allMeters = userData?.smartmeters || [];

  // 1) Get all NON-"Others" smartmeters
  const nonOthersDates = allMeters
    .filter(m => m.usage !== "Others")
    .map(m => new Date(m.sm_installation_date))
    .filter(d => !isNaN(d));  // make sure valid dates

  // 2) Get earliest install date among non-Others
  const earliestNonOthersDate =
    nonOthersDates.length > 0
      ? new Date(Math.min(...nonOthersDates))
      : undefined;

  // 3) Get the "Others" meter install date
  const othersInstallDate = allMeters.find(m => m.usage === "Others")?.sm_installation_date
    ? new Date(allMeters.find(m => m.usage === "Others").sm_installation_date)
    : undefined;

  const allDates = allMeters
    .map(m => new Date(m.sm_installation_date))
    .filter(d => !isNaN(d)); // only valid dates

  // Get the earliest date
  const earliestDateAll = allDates.length > 0
    ? new Date(Math.min(...allDates))
    : undefined;

  // Convert to "YYYY-MM" for month picker
  const earliestMonthAll = earliestDateAll
    ? earliestDateAll.toISOString().slice(0, 7)
    : undefined;

  useEffect(() => {
    // When switching tabs, set the date picker to today's date
    setSelectedDate(new Date());
  }, [activeView]);



  return (
    <>
      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", }}>
        {/* ---------------------------Header-------------------------- */}
        <Box
          className={darkMode ? "header_darkM" : "header_lightM"}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            borderBottom: `1px solid ${darkMode ? colors.d3 : colors.l3}`,
            width: "100%",
            alignItems: "center",
            backgroundColor: darkMode ? colors.d1 : colors.l1,
          }}
        >
          <Box
            sx={{
              width: "30%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "100%",
              marginLeft: "2rem",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: "bold",
                fontSize: { xs: "25px", md: "40px" },
                color: darkMode ? colors.f2 : colors.f3,
              }}
            >
              Dashboard
            </Typography>
          </Box>
        </Box>

        {/* Basic Details */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            marginTop: '1rem'
          }}
        >
          <Box sx={{ width: '97%', height: "50vh", display: "flex", flexDirection: "row", backgroundColor: darkMode ? colors.d2 : colors.l2, boxShadow: 2, borderRadius: "0.7rem", }}>
            {/* Data section */}
            <Box sx={{ width: "60%", height: "100%", flexDirection: "column" }}>
              {/*Top box */}
              <Box sx={{ width: "100%", height: '50%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: "1.5%", paddingBottom: "0.75%" }}>
                {/* ----Saving Box ---- */}
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #6558FE, #7f2bca)", display: 'flex', flexDirection: 'row', width: '64%', height: '100%', borderRadius: '1.5rem',
                  }}>
                  {/* ---- Solar Generation ---- */}
                  <Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'flex-start', width: "50%", height: "100%", flexDirection: 'column' }}>
                    <Box sx={{ width: "100%", height: "40%", position: "relative", paddingLeft: "10%" }}>
                      <SolarPowerIcon
                        sx={{
                          position: "absolute",
                          top: "65%",
                          transform: "translateY(-50%)",
                          height: "70%",
                          width: "auto",
                          color: "#ebebeb",
                        }}
                      />
                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      {loading ? (
                        <Typography
                          sx={{
                            fontSize: { xs: "1.8vh", md: "2.5vh" },
                            fontWeight: 'bold',
                            color: colors.f2,
                          }}
                        >
                          Loading...
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            marginTop: '3%',
                            fontSize: { xs: "1.8vh", md: "2.3vh" },
                            fontWeight: 'bold',
                            color: colors.f2,
                          }}
                        >
                          {(() => {
                            const raw = currentSolarGeneration?.total_solar_generated_kWh;
                            const value = parseFloat(raw);
                            if (raw == null || isNaN(value)) return "N/A";

                            return value > 1000
                              ? `${(value / 1000).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} MWh`
                              : `${value.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} KWh`;
                          })()}
                        </Typography>

                      )}
                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.3vh', md: '1.8vh' },
                          color: "#ebebeb",
                        }}
                      >
                        Monthly Solar Generation
                      </Typography>
                    </Box>
                  </Box>
                  {/* ---- Solar Saving ---- */}
                  <Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'flex-start', width: "50%", height: "100%", flexDirection: 'column' }}>
                    <Box sx={{ width: "100%", height: "40%", position: "relative", paddingLeft: "10%" }}>
                      <SavingsIcon
                        sx={{
                          position: "absolute",
                          top: "65%",
                          transform: "translateY(-50%)",
                          height: "70%",
                          width: "auto",
                          color: "#ebebeb",
                        }}
                      />
                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      {loading ? (
                        <Typography
                          sx={{
                            fontSize: { xs: "1.8vh", md: "2.5vh" },
                            fontWeight: 'bold',
                            color: colors.f2,
                          }}
                        >
                          Loading...
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            marginTop: '3%',
                            fontSize: { xs: "1.8vh", md: "2.3vh" },
                            fontWeight: 'bold',
                            color: colors.f2,
                          }}
                        >
                          {currentBill &&
                            ((userData?.user_type ?? 1) === 1
                              ? currentBill["estimated_original_total_bill"] != null && currentBill["estimated_net_total_bill"] != null
                              : currentBill["estimated_original_total_bill"] != null && currentBill["estimated_before_battery_total_bill"] != null
                            ) ? (
                            `RM ${(
                              currentBill["estimated_original_total_bill"] -
                              ((userData?.user_type ?? 1) === 1
                                ? currentBill["estimated_net_total_bill"]
                                : currentBill["estimated_before_battery_total_bill"])
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          ) : (
                            "N/A"
                          )}
                        </Typography>

                      )}
                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.3vh', md: '1.8vh' },
                          color: "#ebebeb",
                        }}
                      >
                        Solar Saving
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* ---- MD Box ---- */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #ab71df, #7f2bca)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', width: '34%', height: '100%',
                    borderRadius: '1.5rem',
                  }}>
                  <Box sx={{ width: "100%", height: "40%", position: "relative", paddingLeft: "10%" }}>
                    <PowerIcon
                      sx={{
                        position: "absolute",
                        top: "65%",
                        transform: "translateY(-50%)",
                        height: "70%",
                        width: "auto",
                        color: "#ebebeb",
                      }}
                    />
                  </Box>
                  <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row", gap: "35%" }}>
                    <Typography
                      sx={{
                        marginTop: '3%',
                        fontSize: { xs: "1.8vh", md: "2.3vh" },
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: colors.f2,
                        minHeight: "40px",
                      }}
                    >
                      {loading
                        ? "Loading..."
                        : EnergySupplier === "TNB"
                          ? (currentBill?.network_rm != null && currentBill?.capacity_rm != null
                            ? `RM ${(parseFloat(currentBill.network_rm) + parseFloat(currentBill.capacity_rm)).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`
                            : "N/A")
                          : (currentBill?.md_rm != null
                            ? `RM ${parseFloat(currentBill.md_rm).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`
                            : "N/A")
                      }
                    </Typography>


                  </Box>
                  <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row", gap: "25%" }}>
                    {EnergySupplier === "TNB" && (
                      <Typography
                        sx={{
                          fontSize: { xs: '1.3vh', md: '1.8vh' },
                          textAlign: 'center',
                          color: "#ebebeb",
                        }}>
                        Capacity & Network Charge
                      </Typography>
                    )}
                    {EnergySupplier === "NUR" && (
                      <Typography
                        sx={{
                          fontSize: { xs: '1.3vh', md: '1.8vh' },
                          textAlign: 'center',
                          color: "#ebebeb",
                        }}>
                        Maximum Demand
                      </Typography>

                    )}
                  </Box>


                </Box>

              </Box>
              {/* Bottom box */}
              <Box sx={{ width: "100%", height: '50%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: "1.5%", paddingTop: "0.75%" }}>
                {/* ---- Total Bill Box ---- */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #6558FE, #2f22a7)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', width: '34%', height: '100%',
                    borderRadius: '1.5rem',
                  }}>
                  <Box sx={{ width: "100%", height: "40%", position: "relative", paddingLeft: "10%" }}>
                    <ReceiptLongIcon
                      sx={{
                        position: "absolute",
                        top: "65%",
                        transform: "translateY(-50%)",
                        height: "70%",
                        width: "auto",
                        color: "#ebebeb",
                      }}
                    />
                  </Box>
                  <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row", gap: "35%" }}>
                    {loading ? (
                      <Typography
                        sx={{
                          fontSize: { xs: "1.8vh", md: "2.5vh" },
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: colors.f2,
                        }}
                      >
                        Loading...
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          marginTop: '3%',
                          fontSize: { xs: "1.8vh", md: "2.3vh" },
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: colors.f2,
                        }}
                      >
                        {currentBill && currentBill["estimated_net_total_bill"] != null
                          ? `RM ${parseFloat(currentBill["estimated_net_total_bill"]).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}`
                          : "N/A"}
                      </Typography>

                    )}
                  </Box>
                  <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row", gap: "25%" }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '1.3vh', md: '1.8vh' },
                        textAlign: 'center',
                        color: "#ebebeb",
                      }}>
                      Total Bill (Current Month)
                    </Typography>
                  </Box>

                </Box>

                {/* ---- Battery Box ---- */}
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #6646db, #7f2bca)", display: 'flex', flexDirection: 'row', width: '64%', height: '100%',
                    borderRadius: '1.5rem',
                  }}
                >
                  {/* ---- Battery SOC ---- */}
                  <Box sx={{ display: "flex", width: "50%", height: "100%", flexDirection: "column", alignItems: 'flex-start', justifyContent: "center" }}>
                    <Box sx={{ width: "100%", height: "40%", position: "relative", paddingLeft: "7%" }}>
                      <BatteryCharging30Icon
                        sx={{
                          position: "absolute",
                          top: "65%",
                          transform: "translateY(-50%)",
                          height: "70%",
                          width: "auto",
                          color: "#ebebeb",
                        }}
                      />
                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      {loading ? (
                        <Typography
                          sx={{
                            fontSize: { xs: "1.8vh", md: "2.5vh" },
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: colors.f2,
                          }}
                        >
                          Loading...
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            marginTop: '3%',
                            fontSize: { xs: "1.8vh", md: "2.3vh" },
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: colors.f2,
                          }}
                        >
                          {currentBattery && currentBattery["latest_battery_soc"] != null
                            ? `${parseFloat(currentBattery["latest_battery_soc"]).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })} %`
                            : "N/A"}
                        </Typography>

                      )}
                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.3vh', md: '1.8vh' },
                          color: "#ebebeb",
                        }}
                      >
                        Battery SOC
                      </Typography>
                    </Box>
                  </Box>
                  {/* ---- Battery Saving ---- */}
                  <Box sx={{ display: "flex", width: "50%", height: "100%", flexDirection: "column", alignItems: 'flex-start', justifyContent: "center" }}>
                    <Box sx={{ width: "100%", height: "40%", position: "relative", paddingLeft: "10%" }}>
                      <SavingsIcon
                        sx={{
                          position: "absolute",
                          top: "65%",
                          transform: "translateY(-50%)",
                          height: "70%",
                          width: "auto",
                          color: "#ebebeb",
                        }}
                      />

                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      {loading ? (
                        <Typography
                          sx={{
                            fontSize: { xs: "1.8vh", md: "2.5vh" },
                            textAlign: 'left',
                            fontWeight: 'bold',
                            color: colors.f2,
                            flexGrow: 0.18,
                          }}
                        >
                          Loading...
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            marginTop: '3%',
                            fontSize: { xs: "1.8vh", md: "2.3vh" },
                            textAlign: 'left',
                            fontWeight: 'bold',
                            color: colors.f2,
                          }}
                        >
                          {currentBill &&
                            currentBill["estimated_before_battery_total_bill"] != null &&
                            currentBill["estimated_net_total_bill"] != null
                            ? `RM ${(currentBill["estimated_before_battery_total_bill"] - currentBill["estimated_net_total_bill"]).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`
                            : "N/A"}
                        </Typography>

                      )}
                    </Box>
                    <Box sx={{ width: "100%", height: "18%", position: "relative", paddingLeft: "10%", mt: "1%", display: "flex", flexDirection: "row" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.3vh', md: '1.8vh' },
                          color: "#ebebeb",
                        }}
                      >
                        BESS Saving
                      </Typography>
                    </Box>
                  </Box>

                </Box>
              </Box>


            </Box>
            {/* Slides section */}
            <Box sx={{ width: "40%", height: "100%", padding: "1%" }}>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                }}
              >
                {/* Swiper function */}
                <Swiper
                  slidesPerView={1}
                  spaceBetween={100}
                  loop={true}
                  autoplay={{
                    delay: 9000,
                    disableOnInteraction: false,
                  }}
                  pagination={{
                    clickable: true,
                  }}
                  modules={[Pagination, Navigation, Autoplay]}
                  className="mySwiper"
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                  onClick={handleSlideClick}
                >
                  {/* Card Slide for User Details */}
                  <SwiperSlide>
                    <Box
                      sx={{
                        paddingLeft: "5%",
                        borderRadius: '1.5rem',
                        backgroundImage: `url(${darkMode ? slideL : slideD})`,
                        backgroundSize: "cover",
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: "center"
                      }}
                    >
                      <CardContent sx={{ '&:last-child': { paddingBottom: 2 }, }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: "1.3vh", paddingBottom: "none" }}>
                          <Typography variant="h5" gutterBottom
                            sx={{
                              fontWeight: 'bold',
                              fontSize: "1.5vw",
                              color: darkMode ? colors.f3 : colors.f2,
                              textTransform: 'uppercase',
                            }}
                          >
                            {userData?.company_name || "N/A"}
                          </Typography>

                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Company Address:</strong> {userData?.company_address || "N/A"}
                          </Typography>

                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Email:</strong> {userData?.email || "N/A"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Phone:</strong> {userData?.phone_number || "N/A"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Solar System Size:</strong> {userData?.solar_system_size ? `${userData.solar_system_size} kWp` : "N/A"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Solar Inverter Size:</strong> {userData?.solar_inverter_size ? `${userData.solar_inverter_size} kWac` : "N/A"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Battery Capacity:</strong> {userData?.battery_capacity_size ? `${userData.battery_capacity_size} kWh` : "N/A"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Battery Inverter Size:</strong> {userData?.battery_inverter_size ? `${userData.battery_inverter_size} kWac` : "N/A"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.9vw" }} color={darkMode ? colors.f5 : colors.f4}>
                            <strong>Tariff:</strong> {tariffNameMap?.[userData?.tariff] || "N/A"}
                          </Typography>


                        </Box>
                      </CardContent>
                    </Box>
                  </SwiperSlide>
                  <SwiperSlide>
                    <Box
                      sx={{
                        paddingLeft: "5%",
                        borderRadius: '1vw',
                        backgroundImage: `url(${userData?.image && userData.image !== "data:image/jpeg;base64,null"
                          ? userData.image
                          : defaultimage
                          })`,

                        backgroundSize: "cover",
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        height: '100%',
                        display: 'flex',
                      }}
                    >
                    </Box>
                  </SwiperSlide>

                </Swiper>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* -----------------------Button section for different tab display ------------------------*/}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: '110%', marginTop: '1rem', }}>
          <Box sx={{
            width: '97%', height: '110%', padding: 2, backgroundColor: darkMode ? colors.d2 : colors.l2, borderRadius: "16px",
            boxShadow: 2,
          }}>

            <Box sx={{ marginBottom: "1.5rem", marginTop: "1.25rem", display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem" }}>
              {/* Button Component */}
              {[
                { view: "graph", label: "Consumption Analysis", icon: <QueryStatsIcon sx={{ fontSize: "100%", width: "100%", height: "100%", color: colors.i1 }} /> },
                { view: "mdreport", label: "Demand Analysis", icon: <InsertChart sx={{ fontSize: "100%", width: "100%", height: "100%", color: colors.i1 }} /> },
                showIndividualTab && { view: "individual", label: "Component Analysis", icon: <BrightnessHighIcon sx={{ fontSize: "100%", width: "100%", height: "100%", color: colors.i1 }} /> },
                { view: "bill", label: "Bill Analysis", icon: <ReceiptIcon sx={{ fontSize: "100%", width: "100%", height: "100%", color: colors.i1 }} /> },
                { view: "analysis", label: "Carbon Reporting", icon: <Co2Icon sx={{ fontSize: "100%", width: "100%", height: "100%", color: colors.i1 }} /> },


              ].filter(Boolean)
                .map((btn, index) => (
                  <Button
                    key={index}
                    onClick={() => setActiveView(btn.view)}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: { xs: "25%", md: "15%" },
                      border: "1px solid",
                      borderRadius: "10px",
                      borderColor: colors.l4,
                      height: { xs: "14vh", md: "8.5vh" },
                      flexDirection: "row",
                      transition: "background-color 0.3s ease",
                      ...(activeView === btn.view
                        ? { backgroundColor: colors.l2, color: colors.l5 }
                        : {
                          backgroundColor: darkMode ? colors.d1 : colors.l5,
                          color: colors.f2,
                          "&:hover": { backgroundColor: "gray.300" },
                        }),
                    }}
                  >
                    <Box sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: { xs: "12vh", md: "100%" },
                      width: '100%',
                      flexDirection: "row",
                      gap: '0.5rem'
                    }}>
                      {/* Icon Section */}
                      <Box
                        sx={{
                          width: "15%",
                          height: "80%",
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {btn.icon}
                      </Box>

                      {/* Text Section */}
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "2vw", md: "0.9vw" },
                            textAlign: "center",
                            maxWidth: "100%",
                            maxHeight: "100%",
                            overflow: "hidden",
                            fontWeight: "bold",
                          }}
                        >
                          {btn.label}
                        </Typography>
                      </Box>
                    </Box>

                  </Button>
                ))}

            </Box>
            {/* Date Picker */}
            <Box
              sx={{
                marginTop: "1.25rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              {/* DatePicker */}
              <DatePicker
                selected={selectedDate}
                onChange={setSelectedDate}
                dateFormat={
                  ["analysis", "mdreport"].includes(activeView) && solarViewType === "month"
                    ? "yyyy"
                    : (["analysis", "mdreport"].includes(activeView) && solarViewType === "day") || activeView === "bill"
                      ? "MMMM yyyy"
                      : "MMMM d, yyyy"
                }
                yearItemNumber={9}
                showMonthYearPicker={
                  activeView !== "graph" &&
                  activeView !== "individual" &&
                  !(["analysis", "mdreport"].includes(activeView) && solarViewType === "month")
                }
                showYearPicker={
                  ["analysis", "mdreport"].includes(activeView) && solarViewType === "month"
                }
                todayButton={["graph", "individual"].includes(activeView) ? "Today" : undefined}
                maxDate={new Date()}
                minDate={
                  ["individual"].includes(activeView)
                    ? othersInstallDate || undefined
                    : ["analysis", "bill", "mdreport"].includes(activeView)
                      ? earliestMonthAll
                        ? new Date(earliestMonthAll + "-01")  // convert "YYYY-MM" to Date
                        : undefined
                      : ["graph"].includes(activeView)
                        ? earliestNonOthersDate || undefined
                        : undefined
                }

                customInput={
                  <TextField
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: darkMode ? colors.f2 : colors.d5,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: darkMode ? colors.f2 : colors.d5,
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px",
                        border: darkMode ? `2px solid ${colors.f2}` : `2px solid ${colors.l5}`,
                        borderRadius: "4px",
                        color: darkMode ? colors.f2 : colors.l5,
                        textAlign: "center",
                      },
                    }}
                  />
                }
                calendarClassName="custom-calendar"
                popperProps={{
                  sx: {
                    ".react-datepicker__year-wrapper": {
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: "10px",
                      maxWidth: "240px",
                    },
                  },
                }}
              />



              {/* Dropdown Individual Time Interval */}
              {activeView === "individual" && (
                <Select
                  variant="outlined"
                  value={timeInterval}
                  onChange={(e) => setTimeInterval(e.target.value)}
                  displayEmpty
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8px",
                      border: darkMode ? `2px solid ${colors.f2}` : `2px solid ${colors.l5}`,
                      borderRadius: "4px",
                      color: darkMode ? colors.f2 : colors.l5,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: darkMode ? colors.f2 : colors.d5,
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: darkMode ? colors.f2 : colors.d5, // Override the blue border on focus
                    },
                    "& .MuiSelect-select:focus": {
                      backgroundColor: "transparent", // Prevent background color change on focus
                      borderColor: darkMode ? colors.f2 : colors.d5,
                    },
                    "& .MuiSelect-icon": {
                      color: darkMode ? colors.f2 : colors.d5,
                    },
                  }}
                >
                  {timeIntervals.map((interval) => (
                    <MenuItem key={interval.value} value={interval.interval}>
                      {interval.label}
                    </MenuItem>
                  ))}
                </Select>

              )}
              {/* Dropdown Graph Time Interval */}
              {activeView === "graph" && (
                <Select
                  variant="outlined"
                  value={timeInterval}
                  onChange={(e) => setTimeInterval(e.target.value)}
                  displayEmpty
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8px",
                      border: darkMode ? `2px solid ${colors.f2}` : `2px solid ${colors.l5}`,
                      borderRadius: "4px",
                      color: darkMode ? colors.f2 : colors.l5,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: darkMode ? colors.f2 : colors.d5,
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: darkMode ? colors.f2 : colors.d5, // Override the blue border on focus
                    },
                    "& .MuiSelect-select:focus": {
                      backgroundColor: "transparent", // Prevent background color change on focus
                      borderColor: darkMode ? colors.f2 : colors.d5,
                    },
                    "& .MuiSelect-icon": {
                      color: darkMode ? colors.f2 : colors.d5,
                    },
                  }}
                >
                  {timeIntervals.map((interval) => (
                    <MenuItem key={interval.value} value={interval.interval}>
                      {interval.label}
                    </MenuItem>
                  ))}
                </Select>

              )}
              {/* Dropdown for Energy Analysis View */}
              {activeView === "analysis" && activeAnalysisTab !== 0 && (
                <Select
                  value={solarViewType}
                  onChange={(e) => setSolarViewType(e.target.value)}
                  displayEmpty
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8px",
                      border: darkMode ? `2px solid ${colors.f2}` : `2px solid ${colors.d5}`,
                      borderRadius: "4px",
                      color: darkMode ? colors.f2 : colors.d5,
                    },
                    "&:focus": {
                      boxShadow: "0 0 0 3px rgba(76, 29, 149, 0.5)",
                    },
                    "& .MuiSelect-icon": {
                      color: darkMode ? colors.f2 : colors.d5,
                    },
                  }}
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                </Select>
              )}

              {/* Dropdown for MD reporting View */}
              {activeView === "mdreport" && (
                <Select
                  value={solarViewType}
                  onChange={(e) => setSolarViewType(e.target.value)}
                  displayEmpty
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8px",
                      border: darkMode ? `2px solid ${colors.f2}` : `2px solid ${colors.d5}`,
                      borderRadius: "4px",
                      color: darkMode ? colors.f2 : colors.d5,
                    },
                    "&:focus": {
                      boxShadow: "0 0 0 3px rgba(76, 29, 149, 0.5)",
                    },
                    "& .MuiSelect-icon": {
                      color: darkMode ? colors.f2 : colors.d5,
                    },
                  }}
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                </Select>
              )}
            </Box>

            {/* Current Update Time */}
            <Box
              sx={{
                marginBottom: "1rem",
                marginTop: "1rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <div style={{ color: 'red', fontWeight: 'bold', textAlign: 'center', marginTop: '10px' }}>
                Data updated until {formattedDate}
              </div>
            </Box>

            {/* Tab View */}
            {activeView === "graph" && (
              <GraphSection
                selectedDate={selectedDate}
                siteId={siteId}
                timeInterval={timeInterval}
                userType={userType} // send user_type instead
              />
            )}

            {activeView === "bill" && (
              <BillView
                selectedDate={selectedDate}
                siteid={siteId}
                userType={userType} // send user_type
                EnergySupplier={EnergySupplier}
                isPPA={isPPA}
              />
            )}

            {activeView === "analysis" && (
              <AnalysisSection
                selectedDate={selectedDate}
                siteId={siteId}
                solarViewType={solarViewType}
                userType={userType}
                onTabChange={(tab) => setActiveAnalysisTab(tab)}
              />
            )}

            {activeView === "individual" && (
              <IndividualSection
                selectedDate={selectedDate}
                siteId={siteId}
                timeInterval={timeInterval}
                userType={userType} // send user_type
              />
            )}

            {activeView === "mdreport" && (
              <MDReportSection
                selectedDate={selectedDate}
                siteId={siteId}
                solarViewType={solarViewType}
                userType={userType} // send user_type
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;